import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { Button } from 'src/domains/common/components/button/button';
import { Input } from 'src/domains/common/components/input/input';

import { Switch } from 'src/domains/common/components/switch/switch';
import { Calendar } from 'src/domains/common/components/calendar/calendar';
import { RequestDataType } from 'src_server/types/paymentSearch/paymentsSearchTypes';

import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import {
    ADJUSTMENTS_SWITCHES,
    CASINO_SWITCHES,
    DEPOSIT_SWITCHES,
    MANUAL_SWITCHES,
    WITHDRAWAL_SWITCHES,
} from 'src/domains/sportsbook/trading/view/payment-search/PaymentSearch.state';
import { useAppStateContext } from 'src/AppState/AppState';
import { PaymentSearchFormState } from './PaymentSearchForm.state';
import { LoadingV2 } from 'src/components/Loading/LoadingV2';
import { useCommon } from 'src/AppState/Common';
import { Select } from 'src/domains/common/components/select/select';

interface PaymentSearchFormPropType {
    getResults: (data: RequestDataType) => void;
    fromDate: FormInputState<string, string>;
    toDate: FormInputState<string, string>;
    account: FormInputState<string, string>;
    fromAmount: FormInputState<string, string>;
    toAmount: FormInputState<string, string>;
    includeSummary: FormInputState<boolean, boolean>;
    saveReport: FormInputState<boolean, boolean>;
    transactionType: FormInputState<string, string>;
    isChecked: (title: keyof RequestDataType['types'], option: string) => boolean;
    onCheckChange: (title: keyof RequestDataType['types'], option: string) => void;
    onSubmit: () => void;
}

export const PaymentSearchForm = observer('PaymentSearch', (props: PaymentSearchFormPropType) => {
    const appState = useAppStateContext();
    const common = useCommon();
    const [state] = React.useState(() => new PaymentSearchFormState(common, appState.common.trpcClient));

    const {
        fromDate,
        toDate,
        account,
        fromAmount,
        toAmount,
        includeSummary,
        saveReport,
        transactionType,
        isChecked,
        onCheckChange,
        onSubmit,
    } = props;

    return (
        <div className='space-y-6 w-1/2'>
            <span className='text-base font-semibold leading-7'>Payment Search</span>
            <div className='grid w-full grid-cols-2 gap-4'>
                <div className='col-span-1'>
                    <Calendar
                        errors={fromDate.errorsForView}
                        formInputState={fromDate}
                        label='From'
                        placeholder='Pick a date'
                        showTimePicker
                        firstDayOfWeek='monday'
                        allowTyping={true}
                    />
                </div>
                <div className='col-span-1'>
                    <Calendar
                        errors={toDate.errorsForView}
                        formInputState={toDate}
                        label='To'
                        placeholder='Pick a date'
                        showTimePicker
                        firstDayOfWeek='monday'
                        allowTyping={true}
                    />
                </div>
                <div className='col-span-2'>
                    <Input
                        errors={account.errorsForView}
                        formInputState={account}
                        label='Account ID'
                    />
                </div>
                <div className='col-span-2'>
                    <Select
                        formInputState={transactionType}
                        placeholder='All'
                        values={[
                            {
                                label: 'All',
                                value: 'all',
                            },
                            {
                                label: 'Deposit',
                                value: 'deposit',
                            },
                            {
                                label: 'Withdrawal',
                                value: 'withdrawal',
                            },
                            {
                                label: 'Bonus',
                                value: 'bonus',
                            },
                        ]}
                        errors={transactionType.errorsForView}
                        label='Transaction Types'
                    />
                </div>

                <div className='col-span-1'>
                    <Input
                        errors={fromAmount.errorsForView}
                        formInputState={fromAmount}
                        label='Total Stake Min (£)'
                    />
                </div>
                <div className='col-span-1'>
                    <Input
                        errors={toAmount.errorsForView}
                        formInputState={toAmount}
                        label='Total Stake Max (£)'
                    />
                </div>
                <div className='col-span-1 flex flex-col gap-4'>
                    Deposit
                    {DEPOSIT_SWITCHES.map((el) => (
                        <Switch
                            key={el.key}
                            label={el.label}
                            checked={isChecked('deposit', el.key)}
                            onChange={() => onCheckChange('deposit', el.key)}
                        />
                    ))}
                </div>
                <div className='col-span-1 flex flex-col gap-4'>
                    Bonuses
                    {state.isLoading ? (
                        <LoadingV2
                            dataTest='payment-search-bonus-list-root'
                            title='Loading bonuses...'
                        />
                    ) : (
                        state.bonusListResponse?.map((bonus) => {
                            return (
                                <Switch
                                    key={bonus.type}
                                    label={bonus.name}
                                    checked={isChecked('bonuses', bonus.type)}
                                    onChange={() => onCheckChange('bonuses', bonus.type)}
                                />
                            );
                        })
                    )}
                </div>
                <div className='col-span-1 flex flex-col gap-4'>
                    Adjustments
                    {ADJUSTMENTS_SWITCHES.map((el) => (
                        <Switch
                            key={el.key}
                            label={el.label}
                            checked={isChecked('adjustment', el.key)}
                            onChange={() => onCheckChange('adjustment', el.key)}
                        />
                    ))}
                    {MANUAL_SWITCHES.map((el) => (
                        <Switch
                            key={el.key}
                            label={el.label}
                            checked={isChecked('manual', el.key)}
                            onChange={() => onCheckChange('manual', el.key)}
                        />
                    ))}
                    {CASINO_SWITCHES.map((el) => (
                        <Switch
                            key={el.key}
                            label={el.label}
                            checked={isChecked('casino', el.key)}
                            onChange={() => onCheckChange('casino', el.key)}
                        />
                    ))}
                </div>
                <div className='col-span-1 flex flex-col gap-4'>
                    Withdrawal
                    {WITHDRAWAL_SWITCHES.map((el) => (
                        <Switch
                            key={el.key}
                            label={el.label}
                            checked={isChecked('withdrawal', el.key)}
                            onChange={() => onCheckChange('withdrawal', el.key)}
                        />
                    ))}
                </div>
                <div className='col-span-2 flex flex-row items-center py-3 justify-between'>
                    <div className='flex gap-3'>
                        <Switch
                            label='Include Summary'
                            checked={includeSummary.value}
                            onChange={(e) => includeSummary.setValue(e.target.checked)}
                        />
                        <Switch
                            label='Save Report'
                            checked={saveReport.value}
                            onChange={(e) => saveReport.setValue(e.target.checked)}
                        />
                    </div>
                    <Button onClick={onSubmit}>Run Report</Button>
                </div>
            </div>
        </div>
    );
});
